import { platformCenter, staticBoxesOf } from '../map/index.js';
import type { MapDef, Surface } from '../map/types.js';

export type ColliderKind = 'static' | 'platform' | 'crate' | 'vanish';

export interface Collider {
  id: string;
  kind: ColliderKind;
  minX: number;
  minY: number;
  minZ: number;
  maxX: number;
  maxY: number;
  maxZ: number;
  surface: Surface;
}

const CELL = 8;
const key = (cx: number, cz: number) => `${cx},${cz}`;

/**
 * Mundo de colisiones compartido por cliente (predicción, cámara) y servidor (autoridad).
 * Cajas estáticas en una rejilla espacial; plataformas móviles, cajas empujables y
 * plataformas que desaparecen se tratan como colisionadores dinámicos.
 */
export class PhysicsWorld {
  readonly map: MapDef;
  private grid = new Map<string, Collider[]>();
  private platforms: Collider[];
  private vanish: Collider[];
  private crates = new Map<string, Collider>();
  private hidden = new Set<string>();
  private queryStamp = new Map<Collider, number>();
  private stamp = 0;
  time = 0;

  constructor(map: MapDef) {
    this.map = map;
    for (const b of staticBoxesOf(map)) {
      this.addStatic({
        id: b.id,
        kind: 'static',
        minX: b.min[0],
        minY: b.min[1],
        minZ: b.min[2],
        maxX: b.max[0],
        maxY: b.max[1],
        maxZ: b.max[2],
        surface: b.surface ?? 'normal',
      });
    }
    this.platforms = map.movingPlatforms.map((p) => ({
      id: p.id,
      kind: 'platform' as const,
      minX: 0,
      minY: 0,
      minZ: 0,
      maxX: 0,
      maxY: 0,
      maxZ: 0,
      surface: 'normal' as const,
    }));
    this.vanish = map.vanishPlatforms.map((v) => ({
      id: v.id,
      kind: 'vanish' as const,
      minX: v.min[0],
      minY: v.min[1],
      minZ: v.min[2],
      maxX: v.max[0],
      maxY: v.max[1],
      maxZ: v.max[2],
      surface: 'normal' as const,
    }));
    for (const c of map.crates) this.setCrate(c.id, c.pos[0], c.pos[1], c.pos[2], c.size);
    this.setTime(0);
  }

  private addStatic(c: Collider) {
    const x0 = Math.floor(c.minX / CELL);
    const x1 = Math.floor(c.maxX / CELL);
    const z0 = Math.floor(c.minZ / CELL);
    const z1 = Math.floor(c.maxZ / CELL);
    for (let x = x0; x <= x1; x++)
      for (let z = z0; z <= z1; z++) {
        const k = key(x, z);
        let list = this.grid.get(k);
        if (!list) this.grid.set(k, (list = []));
        list.push(c);
      }
  }

  /** Actualiza la posición de las plataformas móviles al instante t (segundos de servidor). */
  setTime(t: number) {
    this.time = t;
    this.map.movingPlatforms.forEach((p, i) => {
      const [x, y, z] = platformCenter(p, t);
      const c = this.platforms[i];
      c.minX = x - p.size[0] / 2;
      c.maxX = x + p.size[0] / 2;
      c.minY = y - p.size[1] / 2;
      c.maxY = y + p.size[1] / 2;
      c.minZ = z - p.size[2] / 2;
      c.maxZ = z + p.size[2] / 2;
    });
  }

  /** Desplazamiento de una plataforma entre dos instantes. */
  platformDelta(id: string, t0: number, t1: number): [number, number, number] {
    const p = this.map.movingPlatforms.find((m) => m.id === id);
    if (!p) return [0, 0, 0];
    const a = platformCenter(p, t0);
    const b = platformCenter(p, t1);
    return [b[0] - a[0], b[1] - a[1], b[2] - a[2]];
  }

  /** Coloca una caja empujable; (x, y, z) es el centro de la base. */
  setCrate(id: string, x: number, y: number, z: number, size = 1.2) {
    const h = size / 2;
    const c = this.crates.get(id) ?? { id, kind: 'crate' as const, minX: 0, minY: 0, minZ: 0, maxX: 0, maxY: 0, maxZ: 0, surface: 'normal' as const };
    c.minX = x - h;
    c.maxX = x + h;
    c.minZ = z - h;
    c.maxZ = z + h;
    c.minY = y;
    c.maxY = y + size;
    this.crates.set(id, c);
  }

  getCrate(id: string): Collider | undefined {
    return this.crates.get(id);
  }

  setHidden(ids: Iterable<string>) {
    this.hidden = new Set(ids);
  }

  isHidden(id: string) {
    return this.hidden.has(id);
  }

  /** Devuelve los colisionadores que solapan la región dada. */
  query(minX: number, minY: number, minZ: number, maxX: number, maxY: number, maxZ: number, out: Collider[]): Collider[] {
    out.length = 0;
    const s = ++this.stamp;
    const x0 = Math.floor(minX / CELL);
    const x1 = Math.floor(maxX / CELL);
    const z0 = Math.floor(minZ / CELL);
    const z1 = Math.floor(maxZ / CELL);
    const test = (c: Collider) =>
      c.maxX > minX && c.minX < maxX && c.maxY > minY && c.minY < maxY && c.maxZ > minZ && c.minZ < maxZ;
    for (let x = x0; x <= x1; x++)
      for (let z = z0; z <= z1; z++) {
        const list = this.grid.get(key(x, z));
        if (!list) continue;
        for (const c of list) {
          if (this.queryStamp.get(c) === s) continue;
          this.queryStamp.set(c, s);
          if (test(c)) out.push(c);
        }
      }
    for (const c of this.platforms) if (test(c)) out.push(c);
    for (const c of this.vanish) if (!this.hidden.has(c.id) && test(c)) out.push(c);
    for (const c of this.crates.values()) if (test(c)) out.push(c);
    return out;
  }

  /** ¿Está libre la región? Opcionalmente ignora un colisionador. */
  isFree(minX: number, minY: number, minZ: number, maxX: number, maxY: number, maxZ: number, ignoreId?: string): boolean {
    const hits = this.query(minX, minY, minZ, maxX, maxY, maxZ, scratch);
    return hits.every((h) => h.id === ignoreId);
  }

  /**
   * Rayo contra todas las cajas (método de slabs). Devuelve la distancia al primer impacto o null.
   * Se usa para que la cámara no atraviese paredes y para apuntar habilidades.
   */
  raycast(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number, maxDist: number, ignoreCrates = false): number | null {
    const ex = ox + dx * maxDist;
    const ey = oy + dy * maxDist;
    const ez = oz + dz * maxDist;
    const hits = this.query(
      Math.min(ox, ex) - 0.01,
      Math.min(oy, ey) - 0.01,
      Math.min(oz, ez) - 0.01,
      Math.max(ox, ex) + 0.01,
      Math.max(oy, ey) + 0.01,
      Math.max(oz, ez) + 0.01,
      scratch,
    );
    let best: number | null = null;
    for (const c of hits) {
      if (ignoreCrates && c.kind === 'crate') continue;
      const t = rayBox(ox, oy, oz, dx, dy, dz, c);
      if (t !== null && t <= maxDist && (best === null || t < best)) best = t;
    }
    return best;
  }

  /** Altura del suelo bajo un punto (para soltar objetos). */
  groundHeightAt(x: number, y: number, z: number): number {
    const d = this.raycast(x, y + 0.5, z, 0, -1, 0, 60, false);
    return d === null ? 0 : y + 0.5 - d;
  }
}

const scratch: Collider[] = [];

function rayBox(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number, c: Collider): number | null {
  let tmin = 0;
  let tmax = Infinity;
  const axes: Array<[number, number, number, number]> = [
    [ox, dx, c.minX, c.maxX],
    [oy, dy, c.minY, c.maxY],
    [oz, dz, c.minZ, c.maxZ],
  ];
  for (const [o, d, mn, mx] of axes) {
    if (Math.abs(d) < 1e-9) {
      if (o < mn || o > mx) return null;
    } else {
      let t1 = (mn - o) / d;
      let t2 = (mx - o) / d;
      if (t1 > t2) [t1, t2] = [t2, t1];
      tmin = Math.max(tmin, t1);
      tmax = Math.min(tmax, t2);
      if (tmin > tmax) return null;
    }
  }
  return tmin;
}
